import { type vec2 } from 'gl-matrix';

import { aPersonCanUseWhiteboards } from '@canvas/capability-summaries';
import { type Box } from '@canvas/math';
import { captureMessageForCapability, CustomerImpact } from '@canvas/sentry-core';

/**
 * The maximum size of an element in the tile map. This is necessary to prevent malicious
 * actors from creating elements that are too large and causing the tile map freeze or
 * consume too much memory.
 *
 * @remarks 2^20 was chosen as it is large enough that no one could practically create an
 * element that large even when fully zoomed out, while not being so large that it would
 * require more than ~10MB of memory to store the element.
 */
export const MAX_ELEMENT_SIZE = 2 ** 20;

export type TileId = string & { __tileId: symbol };
export type ElementId = string & { __elementId: symbol };

/**
 * The visibility changeset produced by the {@link TileMap}
 * when consumers ask for currently visible elements.
 */
export interface VisibilityChangeset<T> {
  /**
   * Elements on a tile currently in the viewport. Elements themselves may not
   * be visible on screen.
   */
  currentlyVisible: Set<T>;
  /**
   * Elements on a tile that became visible, but was not previously visible.
   */
  becameVisible: Set<T>;
  /**
   * Elements on a tile that is no longer visible, but was previously visible.
   */
  becameHidden: Set<T>;
}

export class TileMap<T> {
  public constructor(
    /**
     * The square size of each tile.
     */
    private tileSize: number,
    /**
     * To loookup which elements belong to a tile.
     */
    private tileToElements = new Map<TileId, Set<T>>(),
    /**
     * To lookup which tiles an element belongs to.
     */
    private elementsToTile = new Map<T, Set<TileId>>(),
    /**
     * Elements which are currently visible
     */
    private visibleElements = new Set<T>(),
    /**
     * tiles which are currently visible
     */
    private visibleTiles = new Set<TileId>()
  ) {}

  /**
   * Gets the tiles an element belongs to.
   * @param elementId
   * @returns
   */
  public get(elementId: T) {
    return this.elementsToTile.get(elementId);
  }

  /**
   * Gets the elements in a tile.
   * @param elementId
   * @returns
   */
  public getElements(tileId: TileId) {
    return this.tileToElements.get(tileId);
  }

  /**
   * Adds an element to the tile map based on its bounding box.
   * @description Lazily creates a tile if it does not already exist.
   * @description Elements can exist in multiple tiles.
   * @param elementId
   * @param bbox
   */
  public add(elementId: T, bbox: Box) {
    let visible = false;

    this.walkTiles(bbox, (tileId) => {
      if (this.visibleTiles.has(tileId)) {
        visible = true;
      }

      this.upsertElementToTile(tileId, elementId);
      this.upsertTileToElement(elementId, tileId);
    });

    return visible;
  }

  /**
   * Updates the tiles an element is part of.
   * @description Lazily creates a tile if it does not already exist.
   * @description Elements can exist in multiple tiles.
   * @param elementId
   * @param bbox
   */
  public update(elementId: T, bbox: Box) {
    const currentTiles = this.elementsToTile.get(elementId) ?? new Set();
    const nextTiles = new Set<TileId>();

    let visible = false;

    // We need to:
    // 1) Add the element to the new tiles it is now part of
    // 2) Check if the element is in a visible tile
    // 3) Remove the element from old tiles it is no longer part of

    this.walkTiles(bbox, (tileId) => {
      // Perform 1).
      if (!currentTiles.has(tileId)) {
        this.upsertElementToTile(tileId, elementId);
        this.upsertTileToElement(elementId, tileId);
      }

      // Perform 2)
      if (this.visibleTiles.has(tileId)) {
        visible = true;
      }

      nextTiles.add(tileId);
    });

    currentTiles.forEach((currentTileId) => {
      // Perform 3).
      if (!nextTiles.has(currentTileId)) {
        this.deleteElementFromTile(currentTileId, elementId);
        this.deleteTileFromElement(elementId, currentTileId);
      }
    });

    return visible;
  }

  /**
   * Clears all data for an element.
   * @param elementId
   */
  public delete(elementId: T) {
    const tiles = this.elementsToTile.get(elementId);
    if (!tiles) {
      return;
    }

    tiles.forEach((tile) => this.deleteElementFromTile(tile, elementId));
    this.elementsToTile.delete(elementId);
  }

  private overlappingTilesFlyweight = new Set<TileId>();

  /**
   * Returns all elements that may contain the given point.
   */
  public getElementsContainingPoint(point: vec2, result = new Set<T>()): Set<T> {
    try {
      const overlappingTiles = this.getVisibleTiles(
        {
          left: point[0],
          right: point[0],
          top: point[1],
          bottom: point[1],
        },
        this.overlappingTilesFlyweight
      );

      overlappingTiles.forEach((tileId) => {
        const elementIds = this.tileToElements.get(tileId);
        if (elementIds) {
          elementIds.forEach((elementId) => result.add(elementId));
        }
      });
    } finally {
      this.overlappingTilesFlyweight.clear();
    }

    return result;
  }

  /**
   * Computes which elements are currently visible.
   * @param bbox
   * @returns
   */
  public getVisibleElements(bbox: Box): VisibilityChangeset<T> {
    const visibleTiles = this.getVisibleTiles(bbox);
    const visibleElements = new Set<T>();

    let elementIds: Set<T> | undefined;
    let visibleTilesChanged = false;

    visibleTiles.forEach((tileId) => {
      if (!this.visibleTiles.has(tileId)) {
        visibleTilesChanged = true;
      }

      elementIds = this.tileToElements.get(tileId);
      if (elementIds) {
        elementIds.forEach((elementId) => visibleElements.add(elementId));
      }
    });

    // Early exit if visibile tiles are the same.
    if (!visibleTilesChanged && visibleTiles.size === this.visibleTiles.size) {
      return {
        currentlyVisible: visibleElements,
        becameVisible: new Set<T>(),
        becameHidden: new Set<T>(),
      };
    }

    // Early exit if we are starting with no visible elements.
    // No need to compute a delta in this case.
    if (this.visibleElements.size === 0) {
      this.visibleTiles = visibleTiles;
      this.visibleElements = visibleElements;

      return {
        currentlyVisible: visibleElements,
        becameVisible: visibleElements,
        becameHidden: new Set<T>(),
      };
    }

    return this.computeVisibilityChange(visibleTiles, visibleElements);
  }

  /**
   * Computes a {@link VisibilityChangeset} based on newly visible tiles and elements.
   * @param visibleTiles
   * @param visibleElements
   * @returns
   */
  private computeVisibilityChange(
    visibleTiles: Set<TileId>,
    visibleElements: Set<T>
  ): VisibilityChangeset<T> {
    const becameVisibleElements = new Set<T>(visibleElements);
    const becameHiddenElements = new Set<T>();

    this.visibleElements.forEach((visibleElementId) => {
      if (!visibleElements.has(visibleElementId)) {
        becameHiddenElements.add(visibleElementId);
      }
    });

    this.visibleTiles = visibleTiles;
    this.visibleElements = visibleElements;

    return {
      currentlyVisible: visibleElements,
      becameVisible: becameVisibleElements,
      becameHidden: becameHiddenElements,
    };
  }

  public get size() {
    return this.tileToElements.size;
  }

  public clear() {
    this.tileToElements.clear();
    this.elementsToTile.clear();
  }

  /**
   * Walks the tiles contained in a bounding box.
   * @description Algorithm is based on the element's {@link Box}
   * @param bbox
   * @returns
   */
  private walkTiles(bbox: Box, onTile: (tileId: TileId) => void) {
    // Ensure that we don't end up in an infinite loop if the bounding box is invalid
    const left = Math.min(bbox.left, bbox.right);
    const right = Math.max(bbox.left, bbox.right);
    const bottom = Math.max(bbox.bottom, bbox.top);
    const top = Math.min(bbox.bottom, bbox.top);

    // Validate that the bounding box is not too large
    const width = right - left;
    const height = bottom - top;
    if (
      width > MAX_ELEMENT_SIZE ||
      height > MAX_ELEMENT_SIZE ||
      isNaN(width) ||
      isNaN(height)
    ) {
      const message = `Bounding is too large (${width} x ${height}) to be processed by the tile map`;
      console.error(message);
      captureMessageForCapability(
        message,
        {
          level: 'warning',
          tags: {
            captureLocation: 'TileMap-walkTiles',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        },
        aPersonCanUseWhiteboards
      );

      return;
    }

    const xMinTileId = this.getId(left);
    const xMaxTileId = this.getId(right);
    const yMinTileId = -this.getId(bottom);
    const yMaxTileId = -this.getId(top);

    for (let x = xMinTileId; x <= xMaxTileId; x++) {
      for (let y = yMinTileId; y <= yMaxTileId; y++) {
        onTile(`t:${x}:${y}` as TileId);
      }
    }
  }

  /**
   * Computes which tiles are currently visible.
   * @param bbox
   * @returns
   */
  private getVisibleTiles(bbox: Box, result = new Set<TileId>()): Set<TileId> {
    this.walkTiles(bbox, (tileId) => {
      result.add(tileId);
    });

    return result;
  }

  /**
   * Upserts an element to the {@link tiles} mapping.
   * @param elementId
   * @param tileId
   */
  private upsertTileToElement(elementId: T, tileId: TileId) {
    const tiles = this.elementsToTile.get(elementId);
    this.elementsToTile.set(
      elementId,
      tiles ? tiles.add(tileId) : new Set<TileId>([tileId])
    );
  }

  /**
   * Upserts an element to the {@link elementsToTile} mapping.
   * @param tileId
   * @param elementId
   */
  private upsertElementToTile(tileId: TileId, elementId: T) {
    const elements = this.tileToElements.get(tileId);
    this.tileToElements.set(
      tileId,
      elements ? elements.add(elementId) : new Set<T>([elementId])
    );
  }

  /**
   * Deletes an element from the {@link elementsToTile} mapping.
   * @param tileId
   * @param elementId
   * @returns
   */
  private deleteElementFromTile(tileId: TileId, elementId: T) {
    const elements = this.tileToElements.get(tileId);
    if (!elements) {
      return;
    }

    elements.delete(elementId);
    this.tileToElements.set(tileId, elements);
  }

  /**
   *
   * @param elementId
   * @param tileId
   */
  private deleteTileFromElement(elementId: T, tileId: TileId) {
    const tiles = this.elementsToTile.get(elementId);
    if (!tiles) {
      return;
    }

    tiles.delete(tileId);
    this.elementsToTile.set(elementId, tiles);
  }

  /**
   * Computes the tile ID in which a point exists.
   * @description (0, 0) is the tile in the middle of the screen.
   * @description (1, 0) is the tile one to the right (x grows rightwards).
   * @description (-1, 0) is the tile one to the left (x grows rightwards).
   * @description (0, 1) is the tile one to the bottom (y grows downwards).
   * @description (0, 01) is the tile one to the top (y grows downwards).
   * @param n
   * @returns
   */
  public getId(n: number) {
    if (n > 0) {
      return Math.floor((n + this.tileSize / 2) / this.tileSize);
    } else {
      return Math.ceil((n - this.tileSize / 2) / this.tileSize);
    }
  }
}
